import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Radar, Plus, RefreshCw, Search, BellRing, Eye, Wallet, Loader2, Tag } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { VigilanciaCard } from '../components/vigilancia/VigilanciaCard';
import { AlertasPanel } from '../components/vigilancia/AlertasPanel';
import { AgregarVigilanciaDialog } from '../components/vigilancia/AgregarVigilanciaDialog';
import { EditarVigilanciaDialog } from '../components/vigilancia/EditarVigilanciaDialog';
import { HistorialDialog } from '../components/vigilancia/HistorialDialog';
import { EtiquetasDialog } from '../components/vigilancia/EtiquetasDialog';
import { fmtMoney, mismaEtiqueta } from '../components/vigilancia/vigilanciaUtils';

const FILTROS = [{ k: 'todas', l: 'Todas' }, { k: 'alerta', l: 'En alerta' }, { k: 'pausadas', l: 'Pausadas' }];
const TODAS_ETQ = '__todas__';

const contarEtiquetas = (items) => {
  const m = new Map();
  items.forEach((v) => (v.etiquetas || []).forEach((t) => {
    const k = t.toLowerCase();
    m.set(k, { texto: m.get(k)?.texto || t, n: (m.get(k)?.n || 0) + 1 });
  }));
  return [...m.values()].sort((a, b) => a.texto.localeCompare(b.texto, 'es'));
};

const Kpi = ({ icon: Icon, label, value, tone = 'text-slate-900', testid }) => (
  <Card className="p-4 bg-white border-slate-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
        <p className={`mt-1 text-2xl font-bold tabular-nums ${tone}`} style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={testid}>{value}</p>
      </div>
      <Icon className="h-7 w-7 text-slate-200" />
    </div>
  </Card>
);

const EmptyState = ({ onAdd, puede }) => (
  <Card className="p-12 bg-white border-dashed border-2 border-slate-200 text-center" data-testid="vig-empty">
    <div className="mx-auto mb-4 h-16 w-16 rounded-2xl bg-teal-50 flex items-center justify-center">
      <Radar className="h-8 w-8 text-teal-700" />
    </div>
    <h3 className="text-lg font-bold text-slate-800">Todavía no vigilás ninguna cuenta</h3>
    <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
      Agregá un CVU o CUIT y vas a ver sus límites, cuánto consumió en el día y en el mes, y su saldo disponible.
    </p>
    {puede && <Button className="mt-5 bg-teal-700 hover:bg-teal-800" onClick={onAdd} data-testid="vig-empty-agregar"><Plus size={16} className="mr-1.5" /> Vigilar una cuenta</Button>}
  </Card>
);

export default function VigilanciaCuentas() {
  const [perms, setPerms] = useState({});
  const [items, setItems] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filtro, setFiltro] = useState('todas');
  const [addOpen, setAddOpen] = useState(false);
  const [editVig, setEditVig] = useState(null);
  const [histVig, setHistVig] = useState(null);
  const [delVig, setDelVig] = useState(null);
  const [etqVig, setEtqVig] = useState(null);
  const [etq, setEtq] = useState(TODAS_ETQ);

  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([api.get('/vigilancia/cuentas'), api.get('/vigilancia/alertas')]);
      setItems(c.data.items); setAlertas(a.data.items);
    } catch (e) {
      if (e.response?.status !== 403) toast.error('No se pudo cargar la vigilancia');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { api.get('/vigilancia/permisos').then((r) => setPerms(r.data)).catch(() => {}); load(); }, [load]);

  const chequeando = items.some((v) => v.chequeando);
  useEffect(() => {
    const t = setInterval(load, chequeando ? 4000 : 60000);
    return () => clearInterval(t);
  }, [chequeando, load]);

  const chequear = async (v) => {
    setItems((p) => p.map((x) => (x.id === v.id ? { ...x, chequeando: true } : x)));
    await api.post(`/vigilancia/cuentas/${v.id}/chequear`).catch(() => toast.error('No se pudo iniciar el chequeo'));
  };
  const chequearTodas = async () => {
    const { data } = await api.post('/vigilancia/chequear-todas');
    toast.info(`Chequeando ${data.total} cuenta(s) en Waya…`);
    load();
  };
  const toggle = async (v) => {
    await api.put(`/vigilancia/cuentas/${v.id}`, { activo: !v.activo });
    toast.success(v.activo ? 'Vigilancia pausada' : 'Vigilancia reanudada');
    load();
  };
  const eliminar = async () => {
    await api.delete(`/vigilancia/cuentas/${delVig.id}`);
    toast.success('La cuenta dejó de vigilarse');
    setDelVig(null); load();
  };
  const atender = async (a) => {
    await api.post(`/vigilancia/alertas/${a.id}/atender`).catch(() => {});
    load();
  };

  const etiquetas = useMemo(() => contarEtiquetas(items), [items]);
  useEffect(() => {
    if (etq !== TODAS_ETQ && !etiquetas.some((e) => mismaEtiqueta(e.texto, etq))) setEtq(TODAS_ETQ);
  }, [etiquetas, etq]);
  const filtrarEtiqueta = (t) => setEtq((cur) => (mismaEtiqueta(cur, t) ? TODAS_ETQ : t));

  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return items.filter((v) => (filtro === 'alerta' ? v.alertas_activas > 0 : filtro === 'pausadas' ? !v.activo : true))
      .filter((v) => etq === TODAS_ETQ || (v.etiquetas || []).some((e) => mismaEtiqueta(e, etq)))
      .filter((v) => !t || [v.nombre, v.cvu, v.cuit, v.email, ...(v.etiquetas || [])].some((s) => (s || '').toLowerCase().includes(t)));
  }, [items, q, filtro, etq]);

  const enAlerta = items.filter((v) => v.alertas_activas > 0).length;
  const saldoTotal = items.reduce((s, v) => s + (v.ultimo_dato?.disponible || 0), 0);

  return (
    <div className="vig-scope p-6 lg:p-8 space-y-6 bg-slate-50 min-h-full" data-testid="vigilancia-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">Operaciones</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Radar className="h-9 w-9 text-teal-700" /> Vigilancia de Cuentas
          </h1>
          <p className="text-sm text-slate-500 mt-1">Límites, consumo del día y del mes y saldo disponible de cuentas Waya, con alertas propias para cada una.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={chequearTodas} disabled={!items.length || chequeando} data-testid="vig-chequear-todas-btn">
            <RefreshCw size={16} className={`mr-1.5 ${chequeando ? 'animate-spin' : ''}`} /> Chequear todas
          </Button>
          {perms.crear && (
            <Button onClick={() => setAddOpen(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="vig-agregar-btn">
              <Plus size={16} className="mr-1.5" /> Vigilar cuenta
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Eye} label="Cuentas vigiladas" value={items.length} testid="vig-kpi-total" />
        <Kpi icon={BellRing} label="Cuentas en alerta" value={enAlerta} tone={enAlerta ? 'text-rose-600' : 'text-slate-900'} testid="vig-kpi-en-alerta" />
        <Kpi icon={BellRing} label="Alertas activas" value={alertas.length} tone={alertas.length ? 'text-rose-600' : 'text-slate-900'} testid="vig-kpi-alertas" />
        <Kpi icon={Wallet} label="Saldo total vigilado" value={fmtMoney(saldoTotal)} tone="text-teal-800 text-xl" testid="vig-kpi-saldo" />
      </div>

      <AlertasPanel alertas={alertas} onAtender={atender} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-80">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, CVU, CUIT, mail o cartel" className="pl-9 bg-white" data-testid="vig-filtro-input" />
        </div>
        <div className="flex rounded-full bg-white border border-slate-200 p-1">
          {FILTROS.map((f) => (
            <button key={f.k} onClick={() => setFiltro(f.k)} data-testid={`vig-filtro-${f.k}`}
              className={`px-3.5 py-1 text-xs font-semibold rounded-full transition-colors duration-200 ${filtro === f.k ? 'bg-teal-700 text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              {f.l}
            </button>
          ))}
        </div>
        <Select value={etq} onValueChange={setEtq} disabled={!etiquetas.length}>
          <SelectTrigger className={`w-56 bg-white ${etq !== TODAS_ETQ ? 'border-teal-600 text-teal-800' : ''}`} data-testid="vig-filtro-etiqueta">
            <Tag size={14} className="mr-1.5 shrink-0 text-slate-400" />
            <SelectValue placeholder="Cartel" />
          </SelectTrigger>
          <SelectContent className="vig-scope">
            <SelectItem value={TODAS_ETQ} data-testid="vig-filtro-etiqueta-todas">Todos los carteles</SelectItem>
            {etiquetas.map((e, i) => (
              <SelectItem key={e.texto} value={e.texto} data-testid={`vig-filtro-etiqueta-opt-${i}`}>{e.texto} ({e.n})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>}
      {!loading && !items.length && <EmptyState onAdd={() => setAddOpen(true)} puede={perms.crear} />}
      {!loading && items.length > 0 && (
        <div className="grid gap-5 xl:grid-cols-2" data-testid="vig-grid">
          {visibles.map((v, i) => (
            <VigilanciaCard key={v.id} v={v} index={i} perms={perms} onChequear={chequear} onEditar={setEditVig}
              onHistorial={setHistVig} onToggle={toggle} onEliminar={setDelVig}
              onEtiquetas={setEtqVig} onFiltrarEtiqueta={filtrarEtiqueta} etiquetaActiva={etq === TODAS_ETQ ? null : etq} />
          ))}
          {!visibles.length && <p className="text-sm text-slate-500" data-testid="vig-sin-filtro">No hay cuentas para este filtro.</p>}
        </div>
      )}

      <AgregarVigilanciaDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <EditarVigilanciaDialog vig={editVig} onOpenChange={(o) => !o && setEditVig(null)} onSaved={load} />
      <HistorialDialog vig={histVig} onOpenChange={(o) => !o && setHistVig(null)} />
      <EtiquetasDialog vig={etqVig} sugerencias={etiquetas.map((e) => e.texto)} onOpenChange={(o) => !o && setEtqVig(null)} onSaved={load} />
      <AlertDialog open={!!delVig} onOpenChange={(o) => !o && setDelVig(null)}>
        <AlertDialogContent className="vig-scope" data-testid="vig-eliminar-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Dejar de vigilar a {delVig?.nombre}?</AlertDialogTitle>
            <AlertDialogDescription>Se borran su configuración, alertas e historial de chequeos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-rose-600 hover:bg-rose-700" data-testid="vig-eliminar-confirm">Dejar de vigilar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
